import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  BackgroundColors,
  TextColors,
  ErrorColors,
} from '@picsart/design-system/foundation/colors';
import { Effects } from '@picsart/design-system/foundation/effects';
import { Shadows } from '@picsart/design-system/foundation/shadows';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { StackingContext } from '@picsart/design-system/foundation/stackingContext';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { canHover } from '@picsart/design-system/styleUtils/hoverTouch';

const useStyles = createUseStyles(
  {
    container: {
      width: '100%',
      aspectRatio: '1 / 1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: Spacings.s12,
      backgroundColor: BackgroundColors.base,
      borderRadius: CornerRadius.r12,
      border: [1, 'solid', ErrorColors.base.default],
      overflow: 'hidden',
      position: 'relative',
      padding: Spacings.s16,
      [`@media (max-width: ${Breakpoints.sm - 1}px)`]: {
        borderRadius: CornerRadius.r8,
      },
    },
    dismissButtonWrapper: {
      position: 'absolute',
      top: Spacings.s8,
      right: Spacings.s8,
      display: 'inline-flex',
    },
    dismissIconButton: {
      width: 28,
      height: 28,
      flex: '0 0 28px',
      borderRadius: CornerRadius.r4,
      color: TextColors.tint1,
      transition: `background-color 0.15s ${Easings.easeOut}`,
      [canHover]: {
        '&:hover': {
          backgroundColor: BackgroundColors.tint2,
        },
      },
    },
    errorIcon: {
      width: 32,
      height: 32,
      color: ErrorColors.base.default,
    },
    textBlock: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: Spacings.s4,
      width: '100%',
    },
    presetTitle: {
      fontSize: FontSizes.t3,
      fontWeight: FontWeights.semibold,
      color: TextColors.base,
      maxWidth: '90%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    errorMessage: {
      fontSize: FontSizes.t3,
      fontWeight: FontWeights.medium,
      color: TextColors.tint1,
      textAlign: 'center',
      lineHeight: 1.4,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      maxWidth: '90%',
      wordBreak: 'break-word',
      cursor: 'pointer',
      [canHover]: {
        '&:hover': {
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
        },
      },
    },
    '@keyframes popupFadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    '@keyframes popupSlideUp': {
      from: { opacity: 0, transform: 'translateY(12px) scale(0.96)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
    popupOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: Effects.alpha80,
      zIndex: StackingContext.popup,
      padding: Spacings.s16,
      animation: `$popupFadeIn 0.15s ${Easings.easeOut}`,
    },
    popupContent: {
      position: 'relative',
      maxWidth: 320,
      width: '100%',
      backgroundColor: BackgroundColors.base,
      borderRadius: CornerRadius.r16,
      padding: Spacings.s24,
      boxShadow: Shadows.tint3,
      animation: '$popupSlideUp 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: Spacings.s12,
    },
    popupCloseWrapper: {
      position: 'absolute',
      top: Spacings.s12,
      right: Spacings.s12,
      display: 'inline-flex',
    },
    popupCloseIconButton: {
      width: 32,
      height: 32,
      flex: '0 0 32px',
      borderRadius: CornerRadius.r8,
      color: TextColors.tint1,
      transition: `background-color 0.15s ${Easings.easeOut}, color 0.15s ${Easings.easeOut}`,
      [canHover]: {
        '&:hover': {
          backgroundColor: BackgroundColors.tint2,
          color: TextColors.base,
        },
      },
      '&:active': {
        transform: 'scale(0.95)',
      },
    },
    popupPresetTitle: {
      fontSize: FontSizes.t5,
      fontWeight: FontWeights.semibold,
      color: TextColors.base,
      textAlign: 'center',
      wordBreak: 'break-word',
    },
    popupText: {
      fontSize: FontSizes.t4,
      fontWeight: FontWeights.medium,
      color: TextColors.tint1,
      textAlign: 'center',
      lineHeight: 1.5,
      wordBreak: 'break-word',
      marginTop: Spacings.s4,
    },
  },
  { name: 'card-error' }
);

export default useStyles;
